import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { LayoutDashboard, BarChart3, Megaphone, ClipboardList, Crown, LogOut, KeyRound, User, Anchor, ChevronDown, Shield, Download, LifeBuoy, Smartphone, Activity, ExternalLink, Flag } from 'lucide-react';
import { clearToken, getToken, silentRefresh, changeSuperAdminPassword, updateProfile, mfaSetup, mfaEnable, resetMfa, getSupportUnreadCount, SaUser } from './services/api';
import PasswordInput from './components/PasswordInput';
import LoginPage          from './pages/LoginPage';
import DashboardPage      from './pages/DashboardPage';
import CompanyWizardPage  from './pages/CompanyWizardPage';
import CompanyDetailPage  from './pages/CompanyDetailPage';
import AuditLogPage       from './pages/AuditLogPage';
import SuperAdminsPage    from './pages/SuperAdminsPage';
import StatsPage          from './pages/StatsPage';
import BroadcastPage      from './pages/BroadcastPage';
import DownloadsPage      from './pages/DownloadsPage';
import FeatureFlagsPage   from './pages/FeatureFlagsPage';
import SupportPage        from './pages/SupportPage';
import DevicesPage        from './pages/DevicesPage';
import RemoteControlPage  from './pages/RemoteControlPage';

type ProfileTab = 'profile' | 'password' | 'mfa';

export default function App() {
  const [user, setUser]   = useState<SaUser | null>(null);
  const [ready, setReady] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [tab, setTab]     = useState<ProfileTab>('profile');
  const [editName, setEditName]   = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [profSaving, setProfSaving] = useState(false);
  const [profError, setProfError]   = useState('');
  const [profDone, setProfDone]     = useState(false);
  const [curPw, setCurPw]   = useState('');
  const [newPw, setNewPw]   = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError]   = useState('');
  const [pwDone, setPwDone]     = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mfaQrcode, setMfaQrcode]   = useState('');
  const [mfaSecret, setMfaSecret]   = useState('');
  const [mfaCode, setMfaCode]       = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError]     = useState('');
  const [mfaDone, setMfaDone]       = useState(false);
  const [mfaResetting, setMfaResetting]   = useState(false);
  const [mfaResetDone, setMfaResetDone]   = useState(false);
  const [mfaResetError, setMfaResetError] = useState('');
  const [supportUnread, setSupportUnread] = useState(0);

  // 顧客対応の未読数（30秒ごとにポーリングしてナビにバッジ表示）
  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => getSupportUnreadCount().then(setSupportUnread).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30_000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const init = async () => {
      const stored = localStorage.getItem('sa_user');
      if (stored) {
        try {
          if (!getToken()) {
            const ok = await silentRefresh();
            if (!ok) { clearToken(); localStorage.removeItem('sa_user'); setReady(true); return; }
          }
          setUser(JSON.parse(stored) as SaUser);
        } catch { clearToken(); localStorage.removeItem('sa_user'); }
      }
      setReady(true);
    };
    init();
  }, []);

  function openProfile() {
    if (!user) return;
    setEditName(user.name); setEditEmail(user.email);
    setProfError(''); setProfDone(false);
    setCurPw(''); setNewPw(''); setPwError(''); setPwDone(false);
    setMfaQrcode(''); setMfaSecret(''); setMfaCode(''); setMfaError(''); setMfaDone(false); setMfaEnabled(false); setMfaLoading(false);
    setMfaResetting(false); setMfaResetDone(false); setMfaResetError('');
    setTab('profile'); setProfileOpen(true); setMenuOpen(false);
  }

  async function handleMfaTabOpen() {
    setMfaLoading(true); setMfaError(''); setMfaDone(false); setMfaCode(''); setMfaEnabled(false);
    try {
      const data = await mfaSetup();
      setMfaQrcode(data.qrcode);
      setMfaSecret(data.secret);
    } catch (e) {
      setMfaError(e instanceof Error ? e.message : 'エラーが発生しました');
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleMfaReset() {
    if (!user) return;
    setMfaResetting(true); setMfaResetError(''); setMfaResetDone(false);
    try {
      await resetMfa(user.id);
      setMfaResetDone(true);
    } catch (e) {
      setMfaResetError(e instanceof Error ? e.message : 'エラーが発生しました');
    } finally {
      setMfaResetting(false);
    }
  }

  async function handleMfaEnable() {
    if (!mfaCode || mfaCode.length !== 6) { setMfaError('6桁のコードを入力してください。'); return; }
    setMfaLoading(true); setMfaError('');
    try {
      await mfaEnable(mfaCode);
      setMfaEnabled(true); setMfaDone(true); setMfaCode('');
    } catch (e) {
      setMfaError(e instanceof Error ? e.message : 'エラーが発生しました');
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleProfileSave() {
    const data: { name?: string; email?: string } = {};
    if (editName.trim() !== user?.name)   data.name  = editName.trim();
    if (editEmail.trim() !== user?.email) data.email = editEmail.trim();
    if (!Object.keys(data).length) { setProfileOpen(false); return; }
    setProfSaving(true); setProfError('');
    try {
      const updated = await updateProfile(data);
      const newUser = { ...user!, ...updated };
      setUser(newUser); localStorage.setItem('sa_user', JSON.stringify(newUser));
      setProfDone(true);
    } catch (e) { setProfError(e instanceof Error ? e.message : 'エラーが発生しました'); }
    finally { setProfSaving(false); }
  }

  async function handlePwChange() {
    if (!curPw || newPw.length < 6) { setPwError('現在のパスワードと新しいパスワード（6文字以上）を入力してください。'); return; }
    setPwSaving(true); setPwError('');
    try {
      await changeSuperAdminPassword(curPw, newPw);
      setPwDone(true); setCurPw(''); setNewPw('');
    } catch (e) { setPwError(e instanceof Error ? e.message : 'エラーが発生しました'); }
    finally { setPwSaving(false); }
  }

  if (!ready) return null;
  if (!user) return <LoginPage onLogin={u => { localStorage.setItem('sa_user', JSON.stringify(u)); setUser(u); }} />;

  const isOwner = user.sa_role === 'owner';

  const navItems = [
    { to: '/',           label: 'ダッシュボード', icon: LayoutDashboard, end: true },
    { to: '/stats',      label: '利用状況',       icon: BarChart3 },
    { to: '/support',    label: '顧客対応',       icon: LifeBuoy, badge: supportUnread },
    { to: '/devices',    label: '端末・資材管理', icon: Smartphone },
    { to: '/broadcast',  label: '一括メッセージ', icon: Megaphone },
    { to: '/audit-logs', label: '操作ログ',       icon: ClipboardList },
    { to: '/downloads',  label: 'ダウンロード',   icon: Download },
    { to: '/feature-flags', label: '機能フラグ',  icon: Flag },
    ...(isOwner ? [{ to: '/super-admins', label: '運営管理者の管理', icon: Crown }] : []),
  ];

  return (
    <BrowserRouter>
      <style>{`
        .sa-nav { display: flex; align-items: center; gap: 9px; padding: 7px 10px; border-radius: 7px; font-size: 13px; text-decoration: none; margin-bottom: 1px; color: #64748b; transition: background 0.12s, color 0.12s; }
        .sa-nav:hover { background: rgba(255,255,255,0.06); color: #94a3b8; }
        .sa-nav.active { background: rgba(255,255,255,0.1); color: #e2e8f0; font-weight: 600; }
        .sa-user-btn { width: 100%; display: flex; align-items: center; gap: 9px; background: none; border: none; cursor: pointer; padding: 8px 10px; border-radius: 7px; text-align: left; transition: background 0.12s; }
        .sa-user-btn:hover { background: rgba(255,255,255,0.06); }
      `}</style>

      <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', 'Hiragino Sans', 'Yu Gothic UI', sans-serif" }}>

        {/* ── サイドバー ── */}
        <aside style={{
          width: 216, flexShrink: 0, background: '#111827',
          position: 'fixed', top: 0, left: 0, height: '100vh',
          display: 'flex', flexDirection: 'column', zIndex: 20,
          borderRight: '1px solid rgba(255,255,255,0.05)',
        }}>
          {/* ロゴ */}
          <div style={{ padding: '18px 14px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, background: '#1d4ed8', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Anchor size={16} color="#fff" strokeWidth={2} />
              </div>
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 15, letterSpacing: '-0.3px', lineHeight: 1.2 }}>CATMS</div>
                <div style={{ color: '#475569', fontSize: 10, fontWeight: 500, marginTop: 1 }}>運営コンソール</div>
              </div>
            </div>
          </div>

          {/* ナビ */}
          <nav style={{ flex: 1, padding: '10px 6px', overflowY: 'auto' }}>
            {navItems.map(({ to, label, icon: Icon, end, badge }) => (
              <NavLink key={to} to={to} end={end} className={({ isActive }) => `sa-nav${isActive ? ' active' : ''}`}>
                {({ isActive }) => (
                  <>
                    <Icon size={15} color={isActive ? '#60a5fa' : '#4b5563'} strokeWidth={isActive ? 2.5 : 2} style={{ flexShrink: 0 }} />
                    {label}
                    {(badge ?? 0) > 0 && (
                      <span style={{
                        marginLeft: 'auto', background: '#ef4444', color: '#fff',
                        borderRadius: 8, minWidth: 17, height: 17,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 800, padding: '0 5px',
                      }}>{badge}</span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
            {/* Zabbix 監視（外部・別タブ） */}
            <a href="https://zabbix.unison-sea.com" target="_blank" rel="noreferrer" className="sa-nav">
              <Activity size={15} color="#4b5563" strokeWidth={2} style={{ flexShrink: 0 }} />
              サーバー監視 (Zabbix)
              <ExternalLink size={12} color="#4b5563" style={{ marginLeft: 'auto', flexShrink: 0 }} />
            </a>
          </nav>

          {/* ユーザー */}
          <div style={{ padding: '10px 6px', borderTop: '1px solid rgba(255,255,255,0.05)', position: 'relative' }}>
            <button className="sa-user-btn" onClick={() => setMenuOpen(p => !p)}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#1e3a5f', border: '1px solid #1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#93c5fd', flexShrink: 0 }}>
                {user.name.slice(0, 1)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
                <div style={{ fontSize: 10, color: isOwner ? '#d97706' : '#475569', fontWeight: isOwner ? 600 : 400 }}>{isOwner ? 'オーナー' : '運営管理者'}</div>
              </div>
              <ChevronDown size={12} color="#4b5563" style={{ flexShrink: 0, transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>

            {menuOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMenuOpen(false)} />
                <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 6, right: 6, background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', zIndex: 50, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{user.name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{user.email}</div>
                  </div>
                  {[
                    { icon: User,     label: 'プロフィール編集', action: openProfile },
                    { icon: KeyRound, label: 'パスワード変更', action: () => { openProfile(); setTimeout(() => setTab('password'), 50); } },
                    { icon: Shield,   label: 'MFA設定', action: () => { openProfile(); setTimeout(() => { setTab('mfa'); handleMfaTabOpen(); }, 50); } },
                    { icon: LogOut,   label: 'ログアウト', action: () => { clearToken(); setUser(null); setMenuOpen(false); }, danger: true },
                  ].map(item => (
                    <button key={item.label} onClick={item.action}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: item.danger ? '#ef4444' : '#374151', textAlign: 'left' }}
                      onMouseEnter={e => (e.currentTarget.style.background = item.danger ? '#fef2f2' : '#f9fafb')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                      <item.icon size={14} />
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </aside>

        {/* ── コンテンツ ── */}
        <div style={{ marginLeft: 216, flex: 1, minHeight: '100vh', background: '#f8fafc' }}>
          <main style={{ padding: '32px 32px', maxWidth: 1200, margin: '0 auto', boxSizing: 'border-box' }}>
            <Routes>
              <Route path="/"              element={<DashboardPage saRole={user.sa_role} />} />
              <Route path="/wizard"        element={<CompanyWizardPage />} />
              <Route path="/companies/:id" element={<CompanyDetailPage saRole={user.sa_role} />} />
              <Route path="/stats"         element={<StatsPage />} />
              <Route path="/support"       element={<SupportPage />} />
              <Route path="/assets"        element={<Navigate to="/devices" replace />} />
              <Route path="/devices"       element={<DevicesPage />} />
              <Route path="/devices/:deviceId/remote" element={<RemoteControlPage />} />
              <Route path="/broadcast"     element={<BroadcastPage />} />
              <Route path="/audit-logs"    element={<AuditLogPage />} />
              <Route path="/personal-logs" element={<Navigate to="/audit-logs" replace />} />
              <Route path="/downloads"      element={<DownloadsPage />} />
              <Route path="/feature-flags"  element={<FeatureFlagsPage saRole={user.sa_role} />} />
              <Route path="/super-admins"  element={isOwner ? <SuperAdminsPage currentSaId={user.id} /> : <Navigate to="/" replace />} />
              <Route path="*"              element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </div>

      {/* プロフィール/PW変更モーダル */}
      {profileOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }} onClick={() => setProfileOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.2)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 0 }}>
                {(['profile', 'password', 'mfa'] as ProfileTab[]).map(t => (
                  <button key={t} onClick={() => {
                    setTab(t);
                    if (t === 'mfa' && !mfaQrcode && !mfaLoading) handleMfaTabOpen();
                  }} style={{
                    padding: '6px 14px', fontSize: 13, fontWeight: tab === t ? 600 : 400,
                    color: tab === t ? '#111827' : '#9ca3af', background: tab === t ? '#f3f4f6' : 'none',
                    border: 'none', cursor: 'pointer', borderRadius: 7,
                  }}>
                    {t === 'profile' ? 'プロフィール' : t === 'password' ? 'パスワード変更' : 'MFA設定'}
                  </button>
                ))}
              </div>
              <button onClick={() => setProfileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
            </div>
            <div style={{ padding: '20px 20px 20px' }}>
              {tab === 'profile' && (profDone ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: 44, height: 44, background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div style={{ fontSize: 14, color: '#16a34a', fontWeight: 600, marginBottom: 20 }}>更新しました</div>
                  <button onClick={() => setProfileOpen(false)} style={btnPrimary}>閉じる</button>
                </div>
              ) : (
                <>
                  {profError && <div style={errorBox}>{profError}</div>}
                  <div style={{ marginBottom: 14 }}><label style={lbl}>氏名</label><input value={editName} onChange={e => setEditName(e.target.value)} style={inp} /></div>
                  <div style={{ marginBottom: 20 }}><label style={lbl}>メールアドレス</label><input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} style={inp} /></div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleProfileSave} disabled={profSaving} style={btnPrimary}>{profSaving ? '保存中...' : '保存する'}</button>
                    <button onClick={() => setProfileOpen(false)} style={btnSecondary}>キャンセル</button>
                  </div>
                </>
              ))}
              {tab === 'password' && (pwDone ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: 44, height: 44, background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div style={{ fontSize: 14, color: '#16a34a', fontWeight: 600, marginBottom: 20 }}>パスワードを変更しました</div>
                  <button onClick={() => setProfileOpen(false)} style={btnPrimary}>閉じる</button>
                </div>
              ) : (
                <>
                  {pwError && <div style={errorBox}>{pwError}</div>}
                  <div style={{ marginBottom: 14 }}><label style={lbl}>現在のパスワード</label><PasswordInput autoComplete="current-password" value={curPw} onChange={e => setCurPw(e.target.value)} style={inp} autoFocus /></div>
                  <div style={{ marginBottom: 20 }}><label style={lbl}>新しいパスワード（6文字以上）</label><PasswordInput autoComplete="new-password" value={newPw} onChange={e => setNewPw(e.target.value)} style={inp} /></div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handlePwChange} disabled={pwSaving} style={btnPrimary}>{pwSaving ? '変更中...' : '変更する'}</button>
                    <button onClick={() => setProfileOpen(false)} style={btnSecondary}>キャンセル</button>
                  </div>
                </>
              ))}
              {tab === 'mfa' && (mfaDone ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: 44, height: 44, background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div style={{ fontSize: 14, color: '#16a34a', fontWeight: 600, marginBottom: 8 }}>MFAを有効化しました</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>次回ログインから二段階認証が必要になります</div>
                  <button onClick={() => setProfileOpen(false)} style={btnPrimary}>閉じる</button>
                </div>
              ) : mfaLoading && !mfaQrcode ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7280', fontSize: 13 }}>読み込み中...</div>
              ) : mfaEnabled ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 14, color: '#16a34a', fontWeight: 600 }}>MFAは有効です</div>
                </div>
              ) : (
                <>
                  {mfaError && <div style={errorBox}>{mfaError}</div>}
                  {mfaQrcode && (
                    <>
                      <div style={{ textAlign: 'center', marginBottom: 12 }}>
                        <img src={mfaQrcode} alt="MFA QR Code" style={{ width: 180, height: 180, border: '1px solid #e5e7eb', borderRadius: 8 }} />
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 8 }}>
                        Google AuthenticatorやAuthyでQRコードをスキャンしてください
                      </div>
                      {mfaSecret && (
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: '#475569', textAlign: 'center', marginBottom: 16, fontFamily: 'monospace', letterSpacing: '0.05em', wordBreak: 'break-all' }}>
                          {mfaSecret}
                        </div>
                      )}
                      <div style={{ marginBottom: 16 }}>
                        <label style={lbl}>確認コード（6桁）</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={mfaCode}
                          onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          style={{ ...inp, fontSize: 20, letterSpacing: '0.3em', textAlign: 'center' }}
                          autoFocus
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={handleMfaEnable} disabled={mfaLoading || mfaCode.length !== 6} style={{ ...btnPrimary, opacity: (mfaLoading || mfaCode.length !== 6) ? 0.6 : 1 }}>
                          {mfaLoading ? '処理中...' : '有効化'}
                        </button>
                        <button onClick={() => setProfileOpen(false)} style={btnSecondary}>キャンセル</button>
                      </div>
                    </>
                  )}
                  {isOwner && (
                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>オーナー操作</div>
                      {mfaResetDone ? (
                        <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>MFAを無効化しました</div>
                      ) : (
                        <>
                          {mfaResetError && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{mfaResetError}</div>}
                          <button onClick={handleMfaReset} disabled={mfaResetting}
                            style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 7, padding: '7px 14px', fontSize: 12, color: '#dc2626', cursor: mfaResetting ? 'not-allowed' : 'pointer', opacity: mfaResetting ? 0.6 : 1 }}>
                            {mfaResetting ? '処理中...' : 'MFAを無効化'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              ))}
            </div>
          </div>
        </div>
      )}
    </BrowserRouter>
  );
}

const btnPrimary: React.CSSProperties   = { background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer' };
const lbl: React.CSSProperties  = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inp: React.CSSProperties  = { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#111827' };
const errorBox: React.CSSProperties = { background: '#fef2f2', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 14 };
